import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Checkbox, message } from "antd";
import { updateField } from "../../../redux/slices/employee-slice";
import { UploadOutlined, PaperClipOutlined, DeleteOutlined } from "@ant-design/icons";
import { uploadFile } from "../../../service";

const TimeRulesAttendanceESS = () => {
  const dispatch = useDispatch();
  const form = useSelector((state) => state.employeeForm);
  const [loading, setLoading] = useState({ front: false, back: false });

  const ruleKeyMap = {
    "Attendance Rule Applied": "rule_applied",
    "Missing Attendance": "missing_attendance",
    "Late Arrival": "late_arrival",
    "Hours in a day": "hours_in_day",
    "Leave Restrictions": "leave_ristrictions",
    "Over Time Policy": "overtime_policy",
    "Early Out": "early_out",
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    dispatch(updateField({ name, value: checked }));

    // Enforce mutual exclusivity for attendance options
    if (checked) {
      if (name === 'attendance_web') {
        dispatch(updateField({ name: 'attendance_machine', value: false }));
      } else if (name === 'attendance_machine') {
        dispatch(updateField({ name: 'attendance_web', value: false }));
      }
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      message.error('Please upload an image file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      message.error('File size should be less than 5MB');
      return;
    }

    setLoading(prev => ({ ...prev, [type]: true }));
    try {
      const result = await uploadFile(file);
      if (result.message === 'File uploaded successfully to Cloudinary') {
        const fieldName = type === 'front' ? 'frontSideImg' : 'backSideImg';
        dispatch(updateField({ name: fieldName, value: result.url }));
        message.success('File uploaded successfully');
      } else {
        message.error('File upload failed');
      }
    } catch (error) {
      message.error('Failed to upload file');
      console.error('Upload error:', error);
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleFileDelete = (type) => {
    const fieldName = type === 'front' ? 'frontSideImg' : 'backSideImg';
    dispatch(updateField({ name: fieldName, value: null }));
  };

  return (
    <div className="p-8 ">
      <h1 className="text-2xl font-bold mb-2">
        Time rules, Attendance and ESS Access
      </h1>
      <p className="text-md text-gray-600 mb-6">
        Workplace attendance is whether an employee has shown up for work at the
        appointed hour and time.
      </p>

      <div className="bg-form-bg p-4 rounded-md mb-6">
        <h2 className="font-semibold mb-2">Time Rule :</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(ruleKeyMap).map(([label, key]) => (
            <label key={key} className="flex items-center gap-2">
              <Checkbox
                className="text-white"
                name={key} // Use the mapped key
                checked={form[key]} // Get value from state
                onChange={handleCheckboxChange} // Update Redux store
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-form-bg p-4 rounded-md mb-6">
        <h2 className="font-semibold mb-2">Attendance :</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="w-full">
            <label className="flex items-center gap-2">
              <Checkbox
                className="text-white"
                name="attendance_web"
                checked={form.attendance_web}
                onChange={handleCheckboxChange}
              />
              <span>Enable CheckIn CheckOut Access on Web</span>
            </label>
          </div>
          <div className="w-full">
            <label className="flex items-center gap-2">
              <Checkbox
                className="text-white"
                name="attendance_machine"
                checked={form.attendance_machine}
                onChange={handleCheckboxChange}
              />
              <span>Enable CheckIn CheckOut Access on Machine</span>
            </label>
          </div>
        </div>
      </div>

      <div className="bg-form-bg p-4 rounded-md mb-6">
        <h2 className="font-semibold mb-2">CNIC/Driving License Documents :</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="w-full">
            <label className="block mb-2">Front Side</label>
            <div className="border border-dashed border-gray-500 rounded-md flex justify-between items-center py-2">
              {form.frontSideImg === null && (
                <div className="flex-1 bg-form-bg ml-2 text-md text-gray-600">
                  Please choose a file
                </div>
              )}
              {form.frontSideImg && (
                <div className="flex-1 bg-form-bg ml-2 text-md text-gray-600 flex items-center">
                  <PaperClipOutlined className="mr-1" />
                  {form.frontSideImg.length > 20 ? `${form.frontSideImg.substring(0, 20)}...` : form.frontSideImg}
                  <DeleteOutlined
                    className="ml-2 text-red-500 cursor-pointer"
                    onClick={() => handleFileDelete('front')}
                  />
                </div>
              )}
              <input
                type="file"
                hidden
                id="frontUpload"
                accept=".jpg, .png, .jpeg"
                onChange={(e) => handleFileUpload(e, 'front')}
                disabled={loading.front}
              />
              <button
                className="flex-1 text-md text-gray-600"
                onClick={() => document.getElementById('frontUpload').click()}
                disabled={loading.front}
              >
                <UploadOutlined /> {loading.front ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
          <div className="w-full">
            <label className="block mb-2">Back Side</label>
            <div className="border border-dashed border-gray-500 rounded-md flex justify-between items-center py-2">
              {form.backSideImg === null && (
                <div className="flex-1 bg-form-bg ml-2 text-md text-gray-600">
                  Please choose a file
                </div>
              )}
              {form.backSideImg && (
                <div className="flex-1 bg-form-bg ml-2 text-md text-gray-600 flex items-center">
                  <PaperClipOutlined className="mr-1" />
                  {form.backSideImg.length > 20 ? `${form.backSideImg.substring(0, 20)}...` : form.backSideImg}
                  <DeleteOutlined
                    className="ml-2 text-red-500 cursor-pointer"
                    onClick={() => handleFileDelete('back')}
                  />
                </div>
              )}
              <input
                type="file"
                hidden
                id="backUpload"
                accept=".jpg, .png, .jpeg"
                onChange={(e) => handleFileUpload(e, 'back')}
                disabled={loading.back}
              />
              <button
                className="flex-1 text-md text-gray-600"
                onClick={() => document.getElementById('backUpload').click()}
                disabled={loading.back}
              >
                <UploadOutlined /> {loading.back ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default TimeRulesAttendanceESS;
